import React, { useEffect, useRef, useState } from 'react';
import * as BABYLON from '@babylonjs/core';
import * as GUI from '@babylonjs/gui';
import { DungeonEnemy } from './DungeonEnemy';
import { DungeonDoor } from './DungeonDoor';
import { DungeonExitDoor } from './DungeonExitDoor';
import { DungeonChest } from './DungeonChest';
import { RewardSystem, RewardId } from './rewards';
import { Point } from '@game-libs/exc-libs/src/dungeon-generator';
import { GameInputsMediator } from '@game-libs/inputs-mediator';
import { EventsManager } from '@game-libs/events-manager';
import { MazeCameraController } from './MazeCameraController';
import { DungeonEvents } from '../../types/game-events';
import { DungeonStatsBar } from './DungeonStatsBar';
import { Icon } from '../../gui/Icon';
import styles from './styles.module.scss';

interface MazeCrawlerProps {
  mazeLayout?: number[][];
  enemiesPositions?: Point[];
  treasuresPositions?: Point[];
  dungeonLevel?: number;
  /** Player's gold when they entered the dungeon; used for enemy and chest gold rewards (1%–5% × level × multiplier). */
  playerGoldAtEntry?: number;
  wallHeight?: number;
  cellSize?: number;
  moveSpeed?: number;
  rotationSpeed?: number;
  /** Called when player enters exit tile and fade starts; use to dispatch gold + keys to game context. */
  onDungeonCompleteStart?: (gold: number, grayKeys: number) => void;
  /** Called when fade out ends; use to close the dungeon modal. Pass true if player reached exit (success), false if e.g. torch ran out. */
  onDungeonCompleteEnd?: (completed: boolean) => void;
  /** Called when user clicks Give up in the stats bar; use to close the dungeon modal. */
  onGiveUp?: () => void;
  /** Gray keys owned (from game state); used when opening doors if no keys collected this run. */
  keysFromState?: number;
  /** Called when a door is opened using a key from state (not from run-collected keys). */
  onConsumeGlobalKey?: () => void;
}

const PLAYER_START_TILE = 9;
const TORCH_MAX_SECONDS = 60;

enum DoorEdge {
  LEFT = 'LEFT',
  RIGHT = 'RIGHT',
  TOP = 'TOP',
  BOTTOM = 'BOTTOM',
}

/**
 * Determines which edge of the dungeon the exit door (tile 9) is located on.
 * Returns the edge closest to the door position.
 */
function getDoorEdge(layout: number[][]): DoorEdge | null {
  const doorPos = findTilePosition(layout, PLAYER_START_TILE);
  if (!doorPos) return null;

  const { row, col } = doorPos;
  const rows = layout.length;
  const cols = layout[0].length;

  const distLeft = col;
  const distRight = cols - 1 - col;
  const distTop = row;
  const distBottom = rows - 1 - row;

  const minDist = Math.min(distLeft, distRight, distTop, distBottom);

  if (minDist === distLeft) return DoorEdge.LEFT;
  if (minDist === distRight) return DoorEdge.RIGHT;
  if (minDist === distTop) return DoorEdge.TOP;
  return DoorEdge.BOTTOM;
}

const defaultMazeLayout = [
  [1, 1, 1, 1, 1, 1],
  [9, 0, 0, 0, 0, 1],
  [1, 1, 1, 1, 0, 1],
  [1, 0, 0, 0, 0, 1],
  [1, 0, 0, 0, 0, 1],
  [1, 0, 2, 1, 0, 1],
  [1, 1, 1, 1, 1, 1]
];


const defaultEnemiesPositions = [
  { r: 1, c: 2 },
  { r: 3, c: 4 },
];

function findTilePosition(layout: number[][], tile: number): { row: number; col: number } | null {
  for (let row = 0; row < layout.length; row++) {
    for (let col = 0; col < layout[row].length; col++) {
      if (layout[row][col] === tile) return { row, col };
    }
  }
  return null;
}

/** Initial camera yaw (radians) based on which edge the door is on. */
function getInitialCameraYaw(doorEdge: DoorEdge): number {
  // Camera should face away from the edge where the door is located
  let yaw: number;
  switch (doorEdge) {
    case DoorEdge.LEFT:
      yaw = -Math.PI / 2;  // left edge -> face right (+X)
      break;
    case DoorEdge.RIGHT:
      yaw = Math.PI / 2;   // right edge -> face left (-X)
      break;
    case DoorEdge.TOP:
      yaw = Math.PI;       // top edge -> face down (+Z)
      break;
    case DoorEdge.BOTTOM:
      yaw = 0;             // bottom edge -> face up (-Z)
      break;
  }
  return yaw + Math.PI; // 180° correction
}

/**
 * Determines the rotation for a door at the given position.
 * Checks adjacent tiles to find walls and rotates the door perpendicular to them.
 * Returns rotation in radians (for rotation.y).
 */
function getDoorRotation(layout: number[][], row: number, col: number): number {
  const rows = layout.length;
  const cols = layout[0].length;

  // Check adjacent tiles (top, bottom, left, right)
  const top = row > 0 ? layout[row - 1][col] : 1;
  const bottom = row < rows - 1 ? layout[row + 1][col] : 1;
  const left = col > 0 ? layout[row][col - 1] : 1;
  const right = col < cols - 1 ? layout[row][col + 1] : 1;

  // If walls are on left and right (horizontal walls), door should face horizontally (0 or PI)
  // If walls are on top and bottom (vertical walls), door should face vertically (PI/2 or -PI/2)

  const hasHorizontalWalls = (left === 1 && right === 1);
  const hasVerticalWalls = (top === 1 && bottom === 1);

  if (hasHorizontalWalls) {
    // Door between left and right walls -> face along Z axis
    return 0; // 0 degrees
  } else if (hasVerticalWalls) {
    // Door between top and bottom walls -> face along X axis
    return Math.PI / 2; // 90 degrees
  }

  // Default fallback (shouldn't normally reach here if doors are placed correctly)
  return 0;
}

export const MazeCrawler: React.FC<MazeCrawlerProps> = ({
  mazeLayout = defaultMazeLayout,
  enemiesPositions = defaultEnemiesPositions,
  treasuresPositions = [],
  dungeonLevel = 1,
  playerGoldAtEntry = 0,
  wallHeight = 4,
  cellSize = 4,
  moveSpeed = 0.13,
  rotationSpeed = 0.05,
  onDungeonCompleteStart,
  onDungeonCompleteEnd,
  onGiveUp,
  keysFromState = 0,
  onConsumeGlobalKey,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const hasGoldenKeyRef = useRef(false);
  const goldCollectedRef = useRef(0);
  const grayKeysCollectedRef = useRef(0);
  const keysFromStateRef = useRef(keysFromState);
  const onDungeonCompleteStartRef = useRef(onDungeonCompleteStart);
  const onDungeonCompleteEndRef = useRef(onDungeonCompleteEnd);
  const onConsumeGlobalKeyRef = useRef(onConsumeGlobalKey);
  const startFadeRef = useRef<((withRewards: boolean) => void) | null>(null);
  const isTorchFadeRef = useRef(false);
  keysFromStateRef.current = keysFromState;
  onDungeonCompleteStartRef.current = onDungeonCompleteStart;
  onDungeonCompleteEndRef.current = onDungeonCompleteEnd;
  onConsumeGlobalKeyRef.current = onConsumeGlobalKey;

  // UI state for stats (canvas lives in ref; effect deps exclude these so canvas does not re-run)
  const [torchRemaining, setTorchRemaining] = useState(TORCH_MAX_SECONDS);
  const [goldCollected, setGoldCollected] = useState(0);
  const [grayKeysCollected, setGrayKeysCollected] = useState(0);
  const [hasGoldenKey, setHasGoldenKey] = useState(false);

  type CursorKind = 'default' | 'sword' | 'grayKey' | 'noGrayKeys' | 'goldKey' | 'noGoldKey' | 'canDrag';
  const [cursorKind, setCursorKind] = useState<CursorKind>('default');
  const cursorKindRef = useRef<CursorKind>('default');
  const setCursorKindRef = useRef(setCursorKind);
  setCursorKindRef.current = setCursorKind;

  // Keep refs in sync so exit callback can read current values
  useEffect(() => {
    goldCollectedRef.current = goldCollected;
    grayKeysCollectedRef.current = grayKeysCollected;
  }, [goldCollected, grayKeysCollected]);

  // Torch countdown: -1 every second; when it hits 0, fade out and close dungeon without rewards
  useEffect(() => {
    const interval = setInterval(() => {
      setTorchRemaining((prev) => {
        const next = Math.max(0, prev - 1);
        if (prev > 0 && next === 0) {
          startFadeRef.current?.(false);
        }
        return next;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!canvasRef.current) return;

    // Maze layout is used below for world generation; consider emitting e.g. DUNGEON_LOADED here.
    const canvas = canvasRef.current;
    const engine = new BABYLON.Engine(canvas, true);
    const scene = new BABYLON.Scene(engine);
    scene.clearColor = new BABYLON.Color4(0, 0, 0, 1);

    const startCell = findTilePosition(mazeLayout, PLAYER_START_TILE);
    const doorEdge = getDoorEdge(mazeLayout);

    const cameraX = startCell
      ? startCell.col * cellSize + cellSize / 2
      : cellSize * 1.5;
    const cameraZ = startCell
      ? startCell.row * cellSize + cellSize / 2
      : cellSize * 1.5;
    const camera = new BABYLON.UniversalCamera(
      'camera',
      new BABYLON.Vector3(cameraX, wallHeight / 2, cameraZ),
      scene
    );
    if (doorEdge) {
      camera.rotation.y = getInitialCameraYaw(doorEdge);
    }
    // Start cell used for camera spawn; consider emitting event with spawn position if needed.

    camera.fov = 1.4;
    camera.minZ = 0.1;
    camera.maxZ = 100;
    camera.attachControl(canvas, true);
    camera.ellipsoid = new BABYLON.Vector3(0.5, wallHeight / 3, 0.5);
    camera.checkCollisions = true;

    camera.inputs.removeByType('FreeCameraMouseInput');
    camera.inputs.removeByType('FreeCameraKeyboardMoveInput');

    const hLight = new BABYLON.HemisphericLight("hLight", new BABYLON.Vector3(0, 1, 0), scene);
    hLight.intensity = 1.5;

    // =============================
    // GUI - Create AdvancedDynamicTexture for UI
    // =============================
    const advancedTexture = GUI.AdvancedDynamicTexture.CreateFullscreenUI("UI");
    // Pixelated/crisp rendering for pixel-art GUI (e.g. hearts)
    const guiContext = advancedTexture.getContext() as CanvasRenderingContext2D;
    if (guiContext) {
      guiContext.imageSmoothingEnabled = false;
      guiContext.imageSmoothingQuality = 'low';
    }

    // Create fade overlay for level transitions
    const fadeOverlay = new GUI.Rectangle();
    fadeOverlay.width = "100%";
    fadeOverlay.height = "100%";
    fadeOverlay.background = "black";
    fadeOverlay.alpha = 0;
    fadeOverlay.zIndex = 1000; // On top of everything
    advancedTexture.addControl(fadeOverlay);

    const startFade = (withRewards: boolean) => {
      if (withRewards) {
        onDungeonCompleteStartRef.current?.(goldCollectedRef.current, grayKeysCollectedRef.current);
      }
      isTorchFadeRef.current = !withRewards;
      const fadeDuration = 800;
      const startTime = Date.now();
      const fadeInterval = setInterval(() => {
        const elapsed = Date.now() - startTime;
        const fadeProgress = Math.min(elapsed / fadeDuration, 1);
        fadeOverlay.alpha = fadeProgress;
        if (fadeProgress >= 1) {
          clearInterval(fadeInterval);
          isTorchFadeRef.current = false;
          onDungeonCompleteEndRef.current?.(withRewards);
        }
      }, 16);
    };
    startFadeRef.current = startFade;

    // =============================
    // DITHER SHADER
    // =============================
    BABYLON.Effect.ShadersStore["retroDepthFragmentShader"] = `
      precision highp float;
      varying vec2 vUV;
      uniform sampler2D textureSampler;
      uniform sampler2D depthSampler;
      uniform float lightRadius;
      uniform float darknessRadius;
      uniform float nearBrightness;
      uniform float farBrightness;
      uniform float depthScale;
      uniform float ditherSize;

      float getBayer8x8(vec2 uv) {
        vec2 p = floor(uv);
        int x = int(mod(p.x, 8.0));
        int y = int(mod(p.y, 8.0));
        int m = x + y * 8;
        float b[64];
        b[0]=0./64.;  b[1]=32./64.; b[2]=8./64.;  b[3]=40./64.; b[4]=2./64.;  b[5]=34./64.; b[6]=10./64.; b[7]=42./64.;
        b[8]=48./64.; b[9]=16./64.; b[10]=56./64.;b[11]=24./64.;b[12]=50./64.;b[13]=18./64.;b[14]=58./64.;b[15]=26./64.;
        b[16]=12./64.;b[17]=44./64.;b[18]=4./64.; b[19]=36./64.;b[20]=14./64.;b[21]=46./64.;b[22]=6./64.; b[23]=38./64.;
        b[24]=60./64.;b[25]=28./64.;b[26]=52./64.;b[27]=20./64.;b[28]=62./64.;b[29]=30./64.;b[30]=54./64.;b[31]=22./64.;
        b[32]=3./64.; b[33]=35./64.;b[34]=11./64.;b[35]=43./64.;b[36]=1./64.; b[37]=33./64.;b[38]=9./64.; b[39]=41./64.;
        b[40]=51./64.;b[41]=19./64.;b[42]=59./64.;b[43]=27./64.;b[44]=49./64.;b[45]=17./64.;b[46]=57./64.;b[47]=25./64.;
        b[48]=15./64.;b[49]=47./64.;b[50]=7./64.; b[51]=39./64.;b[52]=13./64.;b[53]=45./64.;b[54]=5./64.; b[55]=37./64.;
        b[56]=63./64.;b[57]=31./64.;b[58]=55./64.;b[59]=23./64.;b[60]=61./64.;b[61]=29./64.;b[62]=53./64.;b[63]=21./64.;
        for (int i = 0; i < 64; i++) { if (i == m) return b[i]; }
        return 0.0;
      }

      void main(void) {
        vec4 baseColor = texture2D(textureSampler, vUV);
        
        // DISCARD transparent pixels so they don't dither
        if (baseColor.a < 0.5) discard;

        float depth = texture2D(depthSampler, vUV).r;
        float dist = depth * depthScale; 
        float vignette = length(vUV - vec2(0.5));
        float farPush = pow(dist / 20.0, 1.5) * 20.0; 
        float curvedDist = farPush + (vignette * 17.0); 
        float t = clamp((curvedDist - lightRadius) / (darknessRadius - lightRadius), 0.0, 1.0);
        
        float brightness = mix(nearBrightness, farBrightness, t);
        
        // Final color output
        vec3 dimmedColor = baseColor.rgb * brightness;
        vec2 pixelPos = gl_FragCoord.xy / ditherSize;
        float ditherThreshold = getBayer8x8(pixelPos);
        float dither = (1.0 - t) > ditherThreshold ? 1.0 : 0.0;
        
        gl_FragColor = vec4(dimmedColor * dither, 1.0);
      }
    `;

    const depthRenderer = scene.enableDepthRenderer(camera);
    const postProcess = new BABYLON.PostProcess(
      "RetroDepthEffect", "retroDepth",
      ["lightRadius", "darknessRadius", "nearBrightness", "farBrightness", "depthScale", "ditherSize"],
      ["depthSampler"], 1.0, camera
    );
    // Make post-process only affect rendering group 0
    postProcess.onActivate = () => {
      scene.setRenderingAutoClearDepthStencil(1, false);
    };

    postProcess.onApply = (effect) => {
      effect.setTexture("depthSampler", depthRenderer.getDepthMap());
      effect.setFloat("depthScale", 220.0);
      effect.setFloat("lightRadius", 9.0);
      effect.setFloat("darknessRadius", 80.0);
      effect.setFloat("nearBrightness", 0.8);
      effect.setFloat("farBrightness", 0.0);
      effect.setFloat("ditherSize", 1.0);
    };

    // =============================
    // MATERIALS 
    // =============================
    const createMaterial = (name: string, url: string, isTransparent = false) => {
      const mat = new BABYLON.StandardMaterial(name, scene);
      const tex = new BABYLON.Texture(url, scene, false, true, BABYLON.Texture.NEAREST_SAMPLINGMODE);
      mat.diffuseTexture = tex;

      if (isTransparent) {
        tex.hasAlpha = true;
        mat.useAlphaFromDiffuseTexture = true; // So alpha test uses PNG alpha for cutoff
        // Alpha-test only (no blend): PNG cutout works, but post-process sees mesh as opaque.
        mat.transparencyMode = BABYLON.Material.MATERIAL_ALPHATEST;
        mat.alphaCutOff = 0.5;
        mat.backFaceCulling = false;   // Visible from both sides (plane)
      }

      mat.emissiveColor = new BABYLON.Color3(0.7, 0.7, 0.7);
      mat.specularColor = BABYLON.Color3.Black();
      return mat;
    };

    const tilesBasePath = 'dungeon-crawler/tiles';

    const wallMat1 = createMaterial('wallMatA', `${tilesBasePath}/wall_1.png`);
    const wallMat2 = createMaterial('wallMatB', `${tilesBasePath}/wall_2.png`);
    const wallMat3 = createMaterial('wallMatA', `${tilesBasePath}/wall_3.png`);
    const wallMat4 = createMaterial('wallMatB', `${tilesBasePath}/wall_4.png`);
    const floorMat1 = createMaterial('floorMatA', `${tilesBasePath}/floor_1.png`);
    const floorMat2 = createMaterial('floorMatB', `${tilesBasePath}/floor_2.png`);
    const floorMat3 = createMaterial('floorMatB', `${tilesBasePath}/floor_3.png`);
    const floorMat4 = createMaterial('floorMatB', `${tilesBasePath}/floor_4.png`);
    const ceilMat1 = createMaterial('ceilMat1', `${tilesBasePath}/ceiling_1.png`);
    const ceilMat2 = createMaterial('ceilMat2', `${tilesBasePath}/ceiling_2.png`);
    const ceilMat3 = createMaterial('ceilMat3', `${tilesBasePath}/ceiling_3.png`);
    const ceilMat4 = createMaterial('ceilMat4', `${tilesBasePath}/ceiling_4.png`);
    const doorMat = createMaterial('doorMat', `${tilesBasePath}/gray_door.png`, true);
    const doorOpenMat = createMaterial('doorOpenMat', `${tilesBasePath}/gray_door_open.png`, true);
    const chestMat = createMaterial('chestMat', `${tilesBasePath}/chest.png`, true);
    const chestOpenMat = createMaterial('chestOpenMat', `${tilesBasePath}/chest_open.png`, true);

    // =============================
    // ENEMIES (one random enemy per entry in enemiesPositions)
    // =============================
    const showEnemyDebug = false; // Set to false to hide debug boxes
    const level = Math.max(1, dungeonLevel);
    const enemyConfigs = [
      {
        id: 'bat' as const,
        level: 1,
        health: 1, yOffset: 1.8,
        spriteCols: 3, spriteRows: 1, size: 2, animationSpeed: 2.7, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'swarm' as const,
        level: 1,
        health: 1, yOffset: 1.3,
        spriteCols: 3, spriteRows: 1, size: 2, animationSpeed: 3, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'blob' as const,
        level: 2,
        health: 2, yOffset: -0.05, spriteCols: 2, spriteRows: 1, size: 2, animationSpeed: 1, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'spider' as const,
        level: 3,
        health: 3, yOffset: 0.05, spriteCols: 2, spriteRows: 1, size: 3, animationSpeed: 1, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'skeleton' as const,
        level: 4,
        health: 4, yOffset: 0.45, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 1.5, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'goblin' as const,
        level: 5,
        health: 5, yOffset: 0.45, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 1.5, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'scorpion' as const,
        level: 6,
        health: 6, yOffset: 0.45, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 1.5, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'ghost' as const,
        level: 7,
        health: 7, yOffset: 0.6, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 1.5, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'snake' as const,
        level: 9,
        health: 9, yOffset: 0.5, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 2.5, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'beholder' as const,
        level: 9,
        health: 9, yOffset: 0.7, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 2, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
      {
        id: 'death' as const,
        level: 10,
        health: 10, yOffset: 0.6, spriteCols: 3, spriteRows: 1, size: 3, animationSpeed: 1.5, renderingGroupId: 0, advancedTexture, camera, tileSize: cellSize, showDebug: showEnemyDebug, dungeonLevel: level, playerGoldAtEntry, onDefeated: (gold: number) => setGoldCollected((prev) => prev + gold)
      },
    ];
    // Spawn enemies at or below dungeon level: level 3 → only level 1–3 enemies; level 10 → all 1–10
    let spawnPool = enemyConfigs.filter((c) => c.level <= level);
    if (spawnPool.length === 0) {
      spawnPool = [...enemyConfigs].sort((a, b) => a.level - b.level).slice(0, 5);
    }
    const enemies: DungeonEnemy[] = [];
    for (const { r: row, c: col } of enemiesPositions) {
      const x = col * cellSize + cellSize / 2;
      const z = row * cellSize + cellSize / 2;
      const position = new BABYLON.Vector3(x, 1.5, z);
      const config = spawnPool[Math.floor(Math.random() * spawnPool.length)];
      enemies.push(new DungeonEnemy(scene, position, config));
    }

    // =============================
    // EXIT DOOR - Find position and store for later
    // =============================
    let exitDoorInstance: DungeonExitDoor | null = null;
    const exitDoorPos = findTilePosition(mazeLayout, PLAYER_START_TILE);

    // =============================
    // DOORS - Collect door positions for later instantiation
    // =============================
    const doorPositions: { row: number; col: number }[] = [];
    for (let row = 0; row < mazeLayout.length; row++) {
      for (let col = 0; col < mazeLayout[row].length; col++) {
        if (mazeLayout[row][col] === 2) {
          doorPositions.push({ row, col });
        }
      }
    }

    // =============================
    // WORLD GENERATION (SUB-TILED)
    // =============================
    const subTileSize = cellSize / 2;

    for (let row = 0; row < mazeLayout.length; row++) {
      for (let col = 0; col < mazeLayout[row].length; col++) {
        const xBase = col * cellSize;
        const zBase = row * cellSize;

        // Walls
        if (mazeLayout[row][col] === 1) {
          const halfW = subTileSize;
          const halfH = wallHeight / 2;
          for (let i = 0; i < 2; i++) {
            for (let j = 0; j < 2; j++) {
              for (let k = 0; k < 2; k++) {
                const wall = BABYLON.MeshBuilder.CreateBox(`w${row}${col}_${i}${j}${k}`, { width: halfW, depth: halfW, height: halfH, wrap: true }, scene);
                wall.position.set(xBase + (i * halfW) + (halfW / 2), (k * halfH) + (halfH / 2), zBase + (j * halfW) + (halfW / 2));
                const randomChoice = Math.random();
                if (randomChoice > 0.5) {
                  wall.material = wallMat1;
                } else if (randomChoice > 0.4) {
                  wall.material = wallMat2;
                } else if (randomChoice > 0.15) {
                  wall.material = wallMat3;
                } else {
                  wall.material = wallMat4;
                }
                wall.checkCollisions = true;
              }
            }
          }
        }

        // Floor & Ceiling
        for (let i = 0; i < 2; i++) {
          for (let j = 0; j < 2; j++) {
            const fSub = BABYLON.MeshBuilder.CreateGround(`f${row}${col}_${i}${j}`, { width: subTileSize, height: subTileSize }, scene);
            fSub.position.set(xBase + (i * subTileSize) + (subTileSize / 2), 0.01, zBase + (j * subTileSize) + (subTileSize / 2));
            const randomChoice = Math.random();
            if (randomChoice > 0.5) {
              fSub.material = floorMat1;
            } else if (randomChoice > 0.4) {
              fSub.material = floorMat2;
            } else if (randomChoice > 0.15) {
              fSub.material = floorMat3;
            } else {
              fSub.material = floorMat4;
            }

            const cSub = BABYLON.MeshBuilder.CreateGround(`c${row}${col}_${i}${j}`, { width: subTileSize, height: subTileSize }, scene);
            cSub.position.set(xBase + (i * subTileSize) + (subTileSize / 2), wallHeight, zBase + (j * subTileSize) + (subTileSize / 2));
            cSub.rotation.x = Math.PI; // Flip to face down
            const ceilChoice = Math.random();
            if (ceilChoice > 0.5) {
              cSub.material = ceilMat1;
            } else if (ceilChoice > 0.4) {
              cSub.material = ceilMat2;
            } else if (ceilChoice > 0.15) {
              cSub.material = ceilMat3;
            } else {
              cSub.material = ceilMat4;
            }
          }
        }
      }
    }

    // =============================
    // CREATE EXIT DOOR INSTANCE
    // =============================
    if (exitDoorPos && doorEdge) {
      const baseX = exitDoorPos.col * cellSize + cellSize / 2;
      const baseZ = exitDoorPos.row * cellSize + cellSize / 2;
      const offset = cellSize / 2;

      let finalX = baseX;
      let finalZ = baseZ;

      switch (doorEdge) {
        case DoorEdge.LEFT:
          finalX = baseX - offset;
          break;
        case DoorEdge.RIGHT:
          finalX = baseX + offset;
          break;
        case DoorEdge.TOP:
          finalZ = baseZ - offset;
          break;
        case DoorEdge.BOTTOM:
          finalZ = baseZ + offset;
          break;
      }

      exitDoorInstance = new DungeonExitDoor({
        position: new BABYLON.Vector3(finalX, wallHeight / 2, finalZ),
        rotation: -getInitialCameraYaw(doorEdge),
        cellSize,
        wallHeight,
        scene,
        camera,
        hasGoldenKey: () => hasGoldenKeyRef.current,
        tileRow: exitDoorPos.row,
        tileCol: exitDoorPos.col,
        showDebug: false,
        onEnterTile: () => startFadeRef.current?.(true),
      });
    }

    // =============================
    // CREATE DOOR INSTANCES
    // =============================
    const showDoorDebug = false; // Set to true to see collision boxes and rays
    const tryOpenDoor = (): boolean => {
      if (grayKeysCollectedRef.current > 0) {
        setGrayKeysCollected((prev) => Math.max(0, prev - 1));
        return true;
      }
      if (keysFromStateRef.current > 0) {
        onConsumeGlobalKeyRef.current?.();
        return true;
      }
      return false;
    };
    const doors: DungeonDoor[] = [];
    for (const { row, col } of doorPositions) {
      const xBase = col * cellSize;
      const zBase = row * cellSize;
      const rotation = getDoorRotation(mazeLayout, row, col);

      const door = new DungeonDoor({
        position: new BABYLON.Vector3(
          xBase + cellSize / 2,
          wallHeight / 2,
          zBase + cellSize / 2
        ),
        rotation,
        cellSize,
        wallHeight,
        material: doorMat,
        openMaterial: doorOpenMat,
        scene,
        camera,
        showDebug: showDoorDebug,
        onTryOpen: tryOpenDoor,
      });

      doors.push(door);
    }

    // =============================
    // CREATE CHEST INSTANCES
    // =============================
    const rewardSystem = new RewardSystem(level, playerGoldAtEntry);

    const chests: DungeonChest[] = [];
    for (const { r: row, c: col } of treasuresPositions) {
      const x = col * cellSize + cellSize / 2;
      const z = row * cellSize + cellSize / 2;
      const position = new BABYLON.Vector3(x, 1.5, z);

      // Get reward for this chest
      const reward = rewardSystem.getNextChestReward();

      const chest = new DungeonChest({
        position,
        closedMaterial: chestMat,
        openMaterial: chestOpenMat,
        scene,
        camera,
        reward,
        getIsFadingOut: () => isTorchFadeRef.current,
        onRewardCollected: (collectedReward) => {
          if (collectedReward.id === RewardId.GOLDEN_KEY) {
            hasGoldenKeyRef.current = true;
            setHasGoldenKey(true);
          } else if (collectedReward.id === RewardId.GOLD) {
            setGoldCollected((prev) => prev + collectedReward.amount);
          } else if (collectedReward.id === RewardId.TORCH) {
            setTorchRemaining(TORCH_MAX_SECONDS);
          } else if (collectedReward.id === RewardId.GRAY_KEY) {
            setGrayKeysCollected((prev) => prev + collectedReward.amount);
          }
        },
        showDebug: false
      });

      chests.push(chest);
    }

    // Map mesh -> cursor target for facing ray (one raycast per frame). Doors/exit/chest only show cursor when closed.
    type CursorTarget =
      | { kind: 'enemy'; getIsInBattle: () => boolean }
      | { kind: 'grayDoor' | 'exit' | 'chest'; getIsOpen: () => boolean };
    const meshToTarget = new Map<BABYLON.AbstractMesh, CursorTarget>();
    for (const e of enemies) {
      const target: CursorTarget = { kind: 'enemy', getIsInBattle: () => e.isInBattle() };
      meshToTarget.set(e.mesh, target);
      const col = e.getCollisionMesh();
      if (col) meshToTarget.set(col, target);
    }
    for (const d of doors) {
      const target: CursorTarget = { kind: 'grayDoor', getIsOpen: () => d.getIsOpen() };
      for (const m of d.getPickableMeshes()) meshToTarget.set(m, target);
    }
    if (exitDoorInstance) {
      const target: CursorTarget = { kind: 'exit', getIsOpen: () => exitDoorInstance.getIsOpen() };
      for (const m of exitDoorInstance.getPickableMeshes()) meshToTarget.set(m, target);
    }
    for (const c of chests) {
      const target: CursorTarget = { kind: 'chest', getIsOpen: () => c.getIsOpen() };
      for (const m of c.getPickableMeshes()) meshToTarget.set(m, target);
    }

    // Map mesh -> Space-key action: open door, open chest, attack enemy, or try open exit
    const meshToInteract = new Map<BABYLON.AbstractMesh, () => void>();
    for (const d of doors) {
      const action = () => d.toggle();
      for (const m of d.getPickableMeshes()) meshToInteract.set(m, action);
    }
    for (const c of chests) {
      const action = () => c.open();
      for (const m of c.getPickableMeshes()) meshToInteract.set(m, action);
    }
    const eventsManager = EventsManager();
    for (const e of enemies) {
      const action = () => {
        if (e.isInBattle() && e.isAlive() && !e.isFlickering()) {
          eventsManager.emit(DungeonEvents.ATTACK_ENEMY);
          e.takeDamage(1);
        }
      };
      meshToInteract.set(e.mesh, action);
      const col = e.getCollisionMesh();
      if (col) meshToInteract.set(col, action);
    }
    if (exitDoorInstance) {
      const action = () => exitDoorInstance!.tryOpen();
      for (const m of exitDoorInstance.getPickableMeshes()) meshToInteract.set(m, action);
    }

    const handleSpaceKey = (evt: KeyboardEvent) => {
      if (evt.key !== ' ' || evt.repeat) return;
      evt.preventDefault();
      const ray = camera.getForwardRay(6);
      const hit = ray ? scene.pickWithRay(ray, (mesh) => meshToTarget.has(mesh)) : null;
      if (!hit?.hit || !hit.pickedMesh) return;
      const target = meshToTarget.get(hit.pickedMesh);
      const interact = meshToInteract.get(hit.pickedMesh);
      if (!target || !interact) return;
      if (target.kind === 'grayDoor' && !target.getIsOpen()) interact();
      else if (target.kind === 'exit' && !target.getIsOpen()) interact();
      else if (target.kind === 'chest' && !target.getIsOpen()) interact();
      else if (target.kind === 'enemy' && target.getIsInBattle()) interact();
    };

    window.addEventListener('keydown', handleSpaceKey);

    // =============================
    // CONTROLS - Using MazeCameraController
    // =============================
    const inputsMediator = GameInputsMediator();

    // Initialize the camera controller
    const cameraController = new MazeCameraController(
      camera,
      scene,
      () => inputsMediator.movementVector(false),
      {
        tileSize: cellSize,
        linearSpeed: cellSize / 0.6,  // Adjust speed based on tile size
        angularSpeed: Math.PI,    // 540 degrees per second for snappy rotation
        initialRotation: doorEdge ? getInitialCameraYaw(doorEdge) : 0,
        showDebugRays: false,
        onFootstep: () => EventsManager().emit(DungeonEvents.FOOTSTEP),
      }
    );

    const beforeRenderObserver = scene.onBeforeRenderObservable.add(() => {
      // Check if any enemy is in battle
      const anyEnemyInBattle = enemies.some(e => e.isAlive() && e.isInBattle());

      // Cursor from facing ray (one raycast per frame); doors/exit/chest only when still closed
      const ray = camera.getForwardRay(6);
      const hit = ray ? scene.pickWithRay(ray, (mesh) => meshToTarget.has(mesh)) : null;
      let nextKind: CursorKind = 'default';
      if (anyEnemyInBattle) {
        nextKind = 'sword';
      } else if (hit?.hit && hit.pickedMesh) {
        const target = meshToTarget.get(hit.pickedMesh);
        if (!target) {
          // no-op
        } else if (target.kind === 'enemy' && target.getIsInBattle()) {
          nextKind = 'sword';
        } else if (target.kind === 'grayDoor' && !target.getIsOpen()) {
          const hasGrayKeys = grayKeysCollectedRef.current > 0 || keysFromStateRef.current > 0;
          nextKind = hasGrayKeys ? 'grayKey' : 'noGrayKeys';
        } else if (target.kind === 'exit' && !target.getIsOpen()) {
          nextKind = hasGoldenKeyRef.current ? 'goldKey' : 'noGoldKey';
        } else if (target.kind === 'chest' && !target.getIsOpen()) {
          nextKind = 'canDrag';
        }
      }
      if (nextKind !== cursorKindRef.current) {
        cursorKindRef.current = nextKind;
        setCursorKindRef.current(nextKind);
      }

      // Update camera controller movement lock based on battle state
      if (cameraController) {
        cameraController.setMovementLocked(anyEnemyInBattle);
      }

      // Get rotation state from camera controller
      const isRotating = cameraController ? cameraController.getIsRotating() : false;

      for (const e of enemies) {
        e.update(camera, isRotating);
      }
      for (const c of chests) {
        c.update(camera);
      }

      // Update exit door to check if player entered the tile
      if (exitDoorInstance) {
        exitDoorInstance.update(camera.position);
      }
    });

    engine.runRenderLoop(() => scene.render());
    const handleResize = () => engine.resize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('keydown', handleSpaceKey);
      window.removeEventListener('resize', handleResize);
      scene.onBeforeRenderObservable.remove(beforeRenderObserver);
      engine.stopRenderLoop();
      cameraController.dispose();
      for (const e of enemies) e.dispose();
      for (const d of doors) d.dispose();
      for (const c of chests) c.dispose();
      if (exitDoorInstance) exitDoorInstance.dispose();
      advancedTexture.dispose();
      scene.dispose();
      engine.dispose();
    };
  }, [mazeLayout, enemiesPositions, wallHeight, cellSize, moveSpeed, rotationSpeed]);

  const torchBarMaxWidthPx = TORCH_MAX_SECONDS;
  const torchPct = (torchRemaining / TORCH_MAX_SECONDS) * 100;

  return (
    <div className={styles.mazeRoot}>
      <div
        className={styles.mazeCanvasWrapper}
        style={{
          cursor:
            cursorKind === 'sword'
              ? 'var(--sword-cursor)'
              : cursorKind === 'grayKey'
                ? 'var(--gray-key-cursor)'
                : cursorKind === 'noGrayKeys'
                  ? 'var(--no-keys-cursor)'
                  : cursorKind === 'goldKey'
                    ? 'var(--gold-key-cursor)'
                    : cursorKind === 'noGoldKey'
                      ? 'var(--no-gold-key-cursor)'
                      : cursorKind === 'canDrag'
                        ? 'var(--can-drag-cursor)'
                        : 'var(--mouse-cursor)',
        }}
      >
        <canvas ref={canvasRef} className={styles.mazeCanvas} />
        <div className={styles.torchHud}>
          <Icon iconId="torch" xl hasBackground={false} />
          <div className={styles.torchTrack} style={{ width: torchBarMaxWidthPx }}>
            <div className={styles.torchFill} style={{ '--torch-pct': `${torchPct}%` } as React.CSSProperties} />
          </div>
        </div>
      </div>
      <DungeonStatsBar
        dungeonLevel={dungeonLevel}
        torchRemaining={torchRemaining}
        goldCollected={goldCollected}
        grayKeysCollected={grayKeysCollected}
        hasGoldenKey={hasGoldenKey}
        onGiveUp={onGiveUp}
      />
    </div>
  );
};